import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PortStage } from '../Canvas/PortStage';
import { summarizeEquipmentPreview } from '../../utils/equipmentPreview';

interface EquipmentPreviewPopoverProps {
  equipment: any;
  savedConfig?: any;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isDark?: boolean;
}

const MAX_PREVIEW_WIDTH = 260;

const EquipmentPreviewPopover: React.FC<EquipmentPreviewPopoverProps> = ({
  equipment,
  savedConfig,
  anchorEl,
  onClose,
  onMouseEnter,
  onMouseLeave,
  isDark = true,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const summary = summarizeEquipmentPreview(equipment, savedConfig);
  const layout = summary.layout;
  const scale = layout ? Math.min(1, MAX_PREVIEW_WIDTH / Math.max(layout.stage.width, 1)) : 1;

  useLayoutEffect(() => {
    if (!anchorEl || !popoverRef.current) return;
    const button = anchorEl.getBoundingClientRect();
    const pop = popoverRef.current.getBoundingClientRect();
    const gap = 8;
    let left = button.left - pop.width - gap;
    let top = button.top;
    if (left < 8) left = button.right + gap;
    if (left + pop.width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - pop.width - 8);
    }
    if (top + pop.height > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - pop.height - 8);
    }
    setPos({ top, left });
  }, [anchorEl, equipment?.id, layout?.stage.width, layout?.stage.height, summary.features.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!anchorEl) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className={`equipment-preview-popover fixed z-[95] w-[280px] rounded-lg border shadow-2xl ${
        isDark ? 'border-slate-600 bg-slate-800' : 'border-gray-200 bg-white'
      }`}
      style={{ top: pos.top, left: pos.left }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={`px-3 py-2 border-b ${isDark ? 'border-slate-700/70' : 'border-gray-200'}`}>
        <div className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{equipment.name}</div>
        <div className={`text-[11px] capitalize ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {equipment.manufacturer}
          {summary.faceLabel ? ` · ${summary.faceLabel}` : ''}
        </div>
      </div>

      <div className="p-3">
        {layout ? (
          <div
            className="mx-auto overflow-hidden rounded-md"
            style={{
              width: layout.stage.width * scale,
              height: layout.stage.height * scale,
            }}
          >
            <div
              style={{
                width: layout.stage.width,
                height: layout.stage.height,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              <PortStage layout={layout} hideNumbers colourizePorts />
            </div>
          </div>
        ) : (
          <div className={`h-16 flex items-center justify-center text-xs rounded-md border border-dashed ${
            isDark ? 'text-gray-500 border-slate-600' : 'text-gray-400 border-gray-300'
          }`}>
            No port layout yet
          </div>
        )}

        <div className="mt-3 space-y-1.5">
          {summary.features.map((feature) => (
            <div key={feature.key} className="flex items-center gap-2 text-xs">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: feature.color }}
              />
              <span className="font-medium" style={{ color: feature.color }}>
                {feature.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EquipmentPreviewPopover;
